import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ClockIcon, ShieldIcon, UsersIcon, ArrowLeftIcon, CheckCircle2Icon } from 'lucide-react';
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0
  })
};
export function DeepAssessmentPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = Intro, 1-5 = Assessment Sections
  const [direction, setDirection] = useState(1);
  // State for selections
  const [devices, setDevices] = useState<string[]>([]);
  const [accounts, setAccounts] = useState({
    pm: '',
    bank: '',
    email: '',
    social: ''
  });
  const [habits, setHabits] = useState({
    scamConfidence: 3,
    updates: ''
  });
  const [household, setHousehold] = useState<string[]>([]);
  const [goals, setGoals] = useState<string[]>([]);
  const goToStep = (newStep: number) => {
    setDirection(newStep > step ? 1 : -1);
    setStep(newStep);
  };
  const handleComplete = () => {
    navigate('/assessment-score');
  };
  const toggleArrayItem = (array: string[], setArray: (val: string[]) => void, item: string) => {
    if (array.includes(item)) {
      setArray(array.filter(i => i !== item));
    } else {
      setArray([...array, item]);
    }
  };
  return <div className="min-h-screen bg-[#f8f8f8] flex flex-col overflow-hidden font-sans" data-id="element-1498" data-mp="h36xm6qj4f9y">
      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-1499" data-mp="c5yjqiceyxei">
        {step === 0 && <motion.div key="intro" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="absolute inset-0 w-full min-h-screen bg-[#1a0647] flex flex-col items-center justify-center px-5 py-6" data-id="element-1500" data-mp="77wvzk41m9wp">
            <div className="w-full max-w-md mx-auto flex flex-col items-center text-center flex-1" data-id="element-1501" data-mp="3aq1je1kyf5vl">
              <img src="https://cdn.magicpatterns.com/uploads/ddFjEWvaDfXw4mBqdFC5Gd/Caveo_Logotype_Horizontal_240x50_White_PNG.png" alt="Caveo" className="h-6 mb-8 opacity-80 mt-4" data-id="element-1502" data-mp="3fnjjd2gzuarg" />

              <h1 className="text-2xl md:text-3xl font-bold text-white mb-3 leading-tight" data-id="element-1503" data-mp="c9p51u6o1q5r">
                Let's map out what your household actually needs.
              </h1>
              <p className="text-[#d0bdf5] text-base mb-8 max-w-sm" data-id="element-1504" data-mp="2g8c0secsf35">
                Takes about 10 minutes. We'll look at your devices, accounts,
                and habits to build a personalized action plan.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 w-full" data-id="element-1505" data-mp="uzx4o2b5pyh7">
                <div className="flex flex-col items-center gap-1.5" data-id="element-1506" data-mp="2fm6bvoy00bm7">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-1507" data-mp="2aowm286p5xxu">
                    <ClockIcon size={20} className="text-[#0EB4CC]" data-id="element-1508" data-mp="2og1fgsjh1tzq" />
                  </div>
                  <span className="text-white text-xs font-medium" data-id="element-1509" data-mp="2kfr93errp1kc">
                    10 minutes
                  </span>
                </div>
                <div className="hidden sm:block w-px h-8 bg-white/20" data-id="element-1510" data-mp="1rqtvhoyi3sxx"></div>
                <div className="flex flex-col items-center gap-1.5" data-id="element-1511" data-mp="1rswtow9t4sdq">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-1512" data-mp="25rwji7923dbh">
                    <ShieldIcon size={20} className="text-[#0EB4CC]" data-id="element-1513" data-mp="20pp395h71b3c" />
                  </div>
                  <span className="text-white text-xs font-medium" data-id="element-1514" data-mp="3h3x9tqlkj9tc">
                    Personalized action plan
                  </span>
                </div>
                <div className="hidden sm:block w-px h-8 bg-white/20" data-id="element-1515" data-mp="bbpnyrflq2gf"></div>
                <div className="flex flex-col items-center gap-1.5" data-id="element-1516" data-mp="gaapqvai4u6z">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center" data-id="element-1517" data-mp="gaj3sqid25jr">
                    <UsersIcon size={20} className="text-[#0EB4CC]" data-id="element-1518" data-mp="l82gvn3boca4" />
                  </div>
                  <span className="text-white text-xs font-medium" data-id="element-1519" data-mp="q9ru99e0p21d">
                    Built for your household
                  </span>
                </div>
              </div>

              <div className="w-full space-y-3 mt-auto" data-id="element-1520" data-mp="z7u2a8w64uy5">
                <button onClick={() => goToStep(1)} className="w-full bg-[#8528e2] text-white font-bold py-3.5 rounded-xl hover:bg-[#7322c4] transition-colors shadow-lg text-base" data-id="element-1521" data-mp="14051f9j34cpn">
                  Start Assessment
                </button>
                <button onClick={() => navigate('/home')} className="w-full bg-transparent text-white/60 font-semibold py-3.5 rounded-xl hover:bg-white/5 transition-colors text-base" data-id="element-1522" data-mp="1hzmjz75setqk">
                  Remind Me Later
                </button>
              </div>

              <p className="text-white/40 text-[10px] mt-4" data-id="element-1523" data-mp="2tgh1bgej9pzn">
                Your answers are private by default. You control what is shared
                with your family.
              </p>
            </div>
          </motion.div>}

        {step > 0 && <motion.div key={`step${step}`} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }} className="absolute inset-0 w-full min-h-screen bg-[#f8f8f8] flex flex-col" data-id="element-1524" data-mp="3m7h81z4917v0">
            {/* Header & Progress */}
            <div className="bg-white border-b border-gray-200 pt-6 pb-3 px-5 sticky top-0 z-10" data-id="element-1525" data-mp="3ygpa8ah4wi3">
              <div className="max-w-md mx-auto" data-id="element-1526" data-mp="1y8f4yg7duts4">
                <div className="flex items-center justify-between mb-3" data-id="element-1527" data-mp="2298t9aoqmalj">
                  <button onClick={() => goToStep(step - 1)} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-1528" data-mp="22bl5r8bq7sr7">
                    <ArrowLeftIcon size={18} data-id="element-1529" data-mp="1ejms4hot1law" />
                  </button>
                  <div className="text-xs font-bold text-[#1a0647] uppercase tracking-wider" data-id="element-1530" data-mp="1jir39dvaht3v">
                    {step === 1 && 'Section 1: Your Devices'}
                    {step === 2 && 'Section 2: Your Accounts'}
                    {step === 3 && 'Section 3: Your Habits'}
                    {step === 4 && 'Section 4: Your Household'}
                    {step === 5 && 'Section 5: Your Goals'}
                    <span className="text-gray-400 ml-1.5 font-medium normal-case" data-id="element-1531" data-mp="34w27gydbevrt">
                      {step} of 5
                    </span>
                  </div>
                  <div className="w-8" data-id="element-1532" data-mp="3ey69nqs2kood"></div> {/* Spacer for centering */}
                </div>
                {/* Progress Bar */}
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-1533" data-mp="3nsca3vwx2jtn">
                  <div className="h-full bg-[#0EB4CC] transition-all duration-500 ease-out" style={{
                width: `${step / 5 * 100}%`
              }} data-id="element-1534" data-mp="3j1vqtv6f03zd"></div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4 hide-scrollbar" data-id="element-1535" data-mp="amc1364yd3p2">
              <div className="max-w-md mx-auto h-full flex flex-col" data-id="element-1536" data-mp="6kqmngcrshcr">
                {step === 1 && <div className="space-y-4" data-id="element-1537" data-mp="2ccvy2qw86g1e">
                    <h2 className="text-xl font-bold text-[#1a0647] mb-4" data-id="element-1538" data-mp="2cdp8q2yb1pot">
                      What types of devices do you have in your home?
                    </h2>
                    <div className="flex flex-wrap gap-2.5" data-id="element-1539" data-mp="2wvpew8vxsfl0">
                      {['iPhone', 'Android', 'Mac', 'Windows PC', 'Chromebook', 'iPad', 'Smart TV', 'Smart Speaker', 'Router'].map(device => <button key={device} onClick={() => toggleArrayItem(devices, setDevices, device)} className={`px-4 py-2.5 rounded-full text-sm font-semibold transition-all border ${devices.includes(device) ? 'bg-[#8528e2] text-white border-[#8528e2] shadow-md' : 'bg-white text-[#111111] border-gray-200 hover:border-[#8528e2]'}`} data-id="element-1540" data-mp="2e092i854zoca">
                          {device}
                        </button>)}
                    </div>
                  </div>}

                {step === 2 && <div className="space-y-6" data-id="element-1541" data-mp="cedbtduaw26j">
                    <div data-id="element-1542" data-mp="7ksb3e1bou3n">
                      <h2 className="text-lg font-bold text-[#1a0647] mb-3" data-id="element-1543" data-mp="7k0qszd6eiso">
                        Do you use a password manager?
                      </h2>
                      <div className="flex gap-2.5" data-id="element-1544" data-mp="3uqt29mff6kau">
                        {['Yes', 'No', 'Not sure'].map(opt => <button key={opt} onClick={() => setAccounts({
                    ...accounts,
                    pm: opt
                  })} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all border ${accounts.pm === opt ? 'bg-[#8528e2] text-white border-[#8528e2] shadow-md' : 'bg-white text-[#111111] border-gray-200 hover:border-[#8528e2]'}`} data-id="element-1545" data-mp="3pq5agyjd9zfk">
                            {opt}
                          </button>)}
                      </div>
                    </div>

                    <div data-id="element-1546" data-mp="10mlu8qzp8xtk">
                      <h2 className="text-lg font-bold text-[#1a0647] mb-3" data-id="element-1547" data-mp="vpv6rilkezy4">
                        Is Two-Factor Authentication (2FA) enabled on:
                      </h2>
                      <div className="space-y-3" data-id="element-1548" data-mp="7xof2wqsbh51">
                        {[{
                    key: 'bank',
                    label: 'Your bank account?'
                  }, {
                    key: 'email',
                    label: 'Your primary email?'
                  }, {
                    key: 'social',
                    label: 'Your social media?'
                  }].map(item => <div key={item.key} className="bg-white p-3.5 rounded-xl border border-gray-200 shadow-sm" data-id="element-1549" data-mp="1vvp5k6qzl0o3">
                            <p className="font-medium text-[#111111] text-sm mb-2.5" data-id="element-1550" data-mp="1mv0thhl5ladu">
                              {item.label}
                            </p>
                            <div className="flex gap-2" data-id="element-1551" data-mp="2epcipd9i69yu">
                              {['Yes', 'No'].map(opt => <button key={opt} onClick={() => setAccounts({
                        ...accounts,
                        [item.key]: opt
                      })} className={`flex-1 py-1.5 rounded-lg text-sm font-semibold transition-all border ${accounts[item.key as keyof typeof accounts] === opt ? 'bg-[#8528e2] text-white border-[#8528e2]' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-1552" data-mp="2jmunqskeg51k">
                                  {opt}
                                </button>)}
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>}

                {step === 3 && <div className="space-y-8" data-id="element-1553" data-mp="2d5t5hgjrul4e">
                    <div data-id="element-1554" data-mp="287htaz607yj0">
                      <h2 className="text-lg font-bold text-[#1a0647] mb-4" data-id="element-1555" data-mp="288sy92b6rcbp">
                        How confident are you in identifying a scam email or
                        text?
                      </h2>
                      <div className="px-2" data-id="element-1556" data-mp="6g4riizc26r6">
                        <input type="range" min="1" max="5" value={habits.scamConfidence} onChange={e => setHabits({
                    ...habits,
                    scamConfidence: parseInt(e.target.value)
                  })} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#8528e2]" data-id="element-1557" data-mp="2fch3meq5vtb" />
                        <div className="flex justify-between mt-3 text-xl" data-id="element-1558" data-mp="16cn66sf0o3ci">
                          <span data-id="element-1559" data-mp="16f12f4lfnlln">😵‍💫</span>
                          <span data-id="element-1560" data-mp="1ae8powm23fvo">🤔</span>
                          <span data-id="element-1561" data-mp="1ad73px3i58z6">😐</span>
                          <span data-id="element-1562" data-mp="1fdd1lv0d7rpp">🙂</span>
                          <span data-id="element-1563" data-mp="1feee83x14m9j">😎</span>
                        </div>
                      </div>
                    </div>

                    <div data-id="element-1564" data-mp="2lvrpef0fh0ph">
                      <h2 className="text-lg font-bold text-[#1a0647] mb-3" data-id="element-1565" data-mp="2qsi3iwso9m89">
                        How often do you update your devices?
                      </h2>
                      <div className="space-y-2.5" data-id="element-1566" data-mp="2pburqydzz470">
                        {['Automatically', 'When I remember', 'Rarely', 'Never'].map(opt => <button key={opt} onClick={() => setHabits({
                    ...habits,
                    updates: opt
                  })} className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all border ${habits.updates === opt ? 'bg-[#f0ebf8] text-[#8528e2] border-[#8528e2] shadow-sm' : 'bg-white text-[#111111] border-gray-200 hover:border-[#8528e2]'}`} data-id="element-1567" data-mp="2u94gscxh5dub">
                            {opt}
                          </button>)}
                      </div>
                    </div>
                  </div>}

                {step === 4 && <div className="space-y-4" data-id="element-1568" data-mp="eezxdx2vy4ry">
                    <h2 className="text-xl font-bold text-[#1a0647] mb-1" data-id="element-1569" data-mp="gjhpgn1p3xo">
                      Who might be at higher risk in your household?
                    </h2>
                    <p className="text-gray-500 text-sm mb-4" data-id="element-1570" data-mp="3mxfka88dfppn">
                      Select all that apply.
                    </p>
                    <div className="space-y-2.5" data-id="element-1571" data-mp="3roxkgu2klm9k">
                      {['Child under 13', 'Teenager', 'Older adult over 65', 'Someone with memory concerns', 'No one'].map(person => <button key={person} onClick={() => {
                  if (person === 'No one') {
                    setHousehold(['No one']);
                  } else {
                    const newArr = household.filter(h => h !== 'No one');
                    toggleArrayItem(newArr, setHousehold, person);
                  }
                }} className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all border flex items-center justify-between ${household.includes(person) ? 'bg-[#f0ebf8] text-[#8528e2] border-[#8528e2] shadow-sm' : 'bg-white text-[#111111] border-gray-200 hover:border-[#8528e2]'}`} data-id="element-1572" data-mp="340eeil6ztlkn">
                          {person}
                          {household.includes(person) && <div className="w-4 h-4 rounded-full bg-[#8528e2] flex items-center justify-center" data-id="element-1573" data-mp="d1rv8lz483kq">
                              <CheckCircle2Icon size={12} className="text-white" data-id="element-1574" data-mp="7zst1ff43cpd" />
                            </div>}
                        </button>)}
                    </div>
                  </div>}

                {step === 5 && <div className="space-y-4" data-id="element-1575" data-mp="2xh5c0gp77ddf">
                    <h2 className="text-xl font-bold text-[#1a0647] mb-1" data-id="element-1576" data-mp="32fgmmtnbhrwp">
                      What would success look like for you?
                    </h2>
                    <p className="text-gray-500 text-sm mb-4" data-id="element-1577" data-mp="37g4okad33t5p">
                      Select all that apply.
                    </p>
                    <div className="space-y-2.5" data-id="element-1578" data-mp="3geg9pwv6lyqr">
                      {['Stop worrying about getting hacked', 'Protect my parents from scams', 'Set up my kids safely online', 'Save money on tech subscriptions'].map(goal => <button key={goal} onClick={() => toggleArrayItem(goals, setGoals, goal)} className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all border flex items-center justify-between ${goals.includes(goal) ? 'bg-[#f0ebf8] text-[#8528e2] border-[#8528e2] shadow-sm' : 'bg-white text-[#111111] border-gray-200 hover:border-[#8528e2]'}`} data-id="element-1579" data-mp="132vlglemo1xi">
                          {goal}
                          {goals.includes(goal) && <div className="w-4 h-4 rounded-full bg-[#8528e2] flex items-center justify-center" data-id="element-1580" data-mp="t6iu3u6k205x">
                              <CheckCircle2Icon size={12} className="text-white" data-id="element-1581" data-mp="o6efyat4dhiv" />
                            </div>}
                        </button>)}
                    </div>
                  </div>}

                {/* Footer CTA */}
                <div className="mt-auto pt-6 pb-4" data-id="element-1582" data-mp="2c603bh33yw2y">
                  <button onClick={() => step === 5 ? handleComplete() : goToStep(step + 1)} className="w-full bg-[#8528e2] text-white font-bold py-3.5 rounded-xl hover:bg-[#7322c4] transition-colors shadow-md text-base" data-id="element-1583" data-mp="1y55qmm9128yh">
                    {step === 5 ? 'See My Action Plan' : 'Continue'}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}